import Message from '../../../myt_chat/chat/models/Message';

// The chat window renders a window of the channel's local history, anchored to an
// absolute timeHandle rather than an index or a count: new messages append and
// older pages prepend around it without moving what the user is looking at.
// `cutoff` 0 means "no cutoff — render everything loaded".
// Messages are always ascending by timeHandle (Dexie sortBy('timeHandle')).

export const visibleFrom = <T extends Pick<Message, 'timeHandle'>>(
  messages: Array<T>,
  cutoff: number
): Array<T> => {
  if (cutoff <= 0) {
    return messages;
  }

  const start = messages.findIndex((message) => message.timeHandle >= cutoff);

  if (start === 0) {
    return messages;
  }

  return start < 0 ? [] : messages.slice(start);
};

// Cutoff for first paint: newest page only, or 0 when the history already fits.
export const initialCutoff = <T extends Pick<Message, 'timeHandle'>>(
  messages: Array<T>,
  pageSize: number
): number => (messages.length > pageSize ? messages[messages.length - pageSize].timeHandle : 0);

// Widen the window by one page. `exhausted` means the local buffer ran out, so
// the caller must ask the server for the next older page.
export const widenWindow = <T extends Pick<Message, 'timeHandle'>>(
  messages: Array<T>,
  cutoff: number,
  pageSize: number
): { cutoff: number; exhausted: boolean } => {
  const shown = visibleFrom(messages, cutoff).length;
  const next = shown + pageSize;

  if (next < messages.length) {
    return { cutoff: messages[messages.length - next].timeHandle, exhausted: false };
  }

  return { cutoff: 0, exhausted: true };
};

// Everything one "Load more" press decides: the new cutoff, and whether the
// local buffer ran dry so the next page has to come off the archive.
export const planLoadOlder = <T extends Pick<Message, 'timeHandle'>>(
  messages: Array<T>,
  cutoff: number,
  pageSize: number,
  hasPreviousMessages: boolean
): { cutoff: number; fetchOlder: boolean } => {
  const widened = widenWindow(messages, cutoff, pageSize);

  return { cutoff: widened.cutoff, fetchOlder: widened.exhausted && hasPreviousMessages };
};

// A list that grew while its oldest message got older is an older-page prepend,
// not an incoming message — the view must not jump to the bottom for it.
export const isOlderPagePrepend = (previousOldest: number, nextOldest: number): boolean =>
  previousOldest > 0 && nextOldest > 0 && nextOldest < previousOldest;

// Offer more history only when the conversation actually runs past one page:
// either the window is hiding something local, or the archive came back full so
// an older page may exist. `hasPreviousMessages` alone is not enough — it starts
// true on a fresh channel and only flips false once a partial page comes back.
export const shouldOfferOlder = (
  total: number,
  shown: number,
  pageSize: number,
  hasPreviousMessages: boolean
): boolean => total > shown || (total >= pageSize && hasPreviousMessages);
